/**
 * Generic media rail for index-page detail panels: one hero image with a
 * thumbnail strip and a source switcher. Pass sources (label + positioned
 * images); empty sources are dropped, and no images shows a placeholder.
 * See docs/expandable-index-pages.md.
 */
import { useState } from 'react';
import { Box, Dialog, Stack, Tab, Tabs, Typography } from '@mui/material';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';

import { SectionLabel } from './primitives';

export interface MediaSource {
  key: string;
  label: string;
  /** [position, url] pairs */
  images: Array<[string, string]>;
}

export const MediaGallery = ({
  sources,
  title = 'Photos',
  emptyLabel = 'No photos yet',
}: {
  sources: MediaSource[];
  title?: string;
  emptyLabel?: string;
}) => {
  const populated = sources.filter(source => source.images.length > 0);
  const [sourceIndex, setSourceIndex] = useState(0);
  const [positionIndex, setPositionIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  const source = populated[Math.min(sourceIndex, populated.length - 1)];
  const current = source?.images[Math.min(positionIndex, (source?.images.length ?? 1) - 1)];

  if (!populated.length) {
    return (
      <Box>
        <SectionLabel>{title}</SectionLabel>
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{
            width: 220,
            height: 220,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider',
            color: 'text.disabled',
            bgcolor: 'grey.50',
          }}
        >
          <HideImageOutlinedIcon />
          <Typography variant="caption">{emptyLabel}</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 236 }}>
      <SectionLabel>{title}</SectionLabel>
      <Box
        component="img"
        src={current?.[1]}
        alt={`${source.label} — ${current?.[0]}`}
        onClick={() => setZoomed(true)}
        sx={{
          width: 220,
          height: 220,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'zoom-in',
          display: 'block',
        }}
      />
      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
        {source.images.map(([position, url], index) => (
          <Box key={position} sx={{ textAlign: 'center' }}>
            <Box
              component="img"
              src={url}
              alt={position}
              onClick={() => setPositionIndex(index)}
              sx={{
                width: 44,
                height: 44,
                objectFit: 'cover',
                borderRadius: 0.75,
                cursor: 'pointer',
                display: 'block',
                border: '2px solid',
                borderColor: index === positionIndex ? 'primary.main' : 'divider',
              }}
            />
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{position}</Typography>
          </Box>
        ))}
      </Stack>
      {populated.length > 1 && (
        <Tabs
          value={Math.min(sourceIndex, populated.length - 1)}
          onChange={(_event, value: number) => { setSourceIndex(value); setPositionIndex(0); }}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 30,
            mt: 0.5,
            '& .MuiTab-root': { minHeight: 30, minWidth: 0, px: 1, py: 0.5, fontSize: 11.5 },
          }}
        >
          {populated.map(({ key, label, images }) => (
            <Tab key={key} label={`${label} (${images.length})`} />
          ))}
        </Tabs>
      )}
      <Dialog open={zoomed} onClose={() => setZoomed(false)} maxWidth="md">
        {current && (
          <Box component="img" src={current[1]} alt="Zoomed" sx={{ maxWidth: '100%', maxHeight: '80vh' }} />
        )}
      </Dialog>
    </Box>
  );
};

export default MediaGallery;
